import { useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { GET_PENDING_SUBMISSIONS, GET_ALL_SUBMISSIONS } from "../api/review";
import ResponsiveCDNImage from "../../../../components/common/image/responsive-cdn-image";
import type {
  MissionAdminAllSubmissionsQuery,
  MissionAdminAllSubmissionsQueryVariables,
  MissionAdminPendingSubmissionsQuery,
  MissionAdminPendingSubmissionsQueryVariables,
} from "../../../../__generated__/graphql";

type PendingSubmission =
  MissionAdminPendingSubmissionsQuery["missionAdminPendingSubmissions"][number];
type AllSubmission =
  MissionAdminAllSubmissionsQuery["missionAdminAllSubmissions"][number];
type Submission = PendingSubmission | AllSubmission;

interface UserGroup {
  user: Submission["participation"]["user"];
  participationId: number;
  submissions: Submission[];
}

interface CampaignGroup {
  campaign: Submission["participation"]["run"]["campaign"];
  users: UserGroup[];
}

const SubmissionsList = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"pending" | "all">("pending");
  const [filter, setFilter] = useState({
    campaignId: null as number | null,
    userId: null as number | null,
  });

  const {
    data: pendingData,
    loading: pendingLoading,
    error: pendingError,
    refetch: refetchPending,
  } = useQuery<
    MissionAdminPendingSubmissionsQuery,
    MissionAdminPendingSubmissionsQueryVariables
  >(GET_PENDING_SUBMISSIONS, {
    variables: {
      filter: {
        ...(filter.campaignId && { campaignId: filter.campaignId }),
        ...(filter.userId && { userId: filter.userId }),
      },
    },
    fetchPolicy: "network-only",
    skip: activeTab === "all",
  });

  const {
    data: allData,
    loading: allLoading,
    error: allError,
    refetch: refetchAll,
  } = useQuery<
    MissionAdminAllSubmissionsQuery,
    MissionAdminAllSubmissionsQueryVariables
  >(GET_ALL_SUBMISSIONS, {
    variables: {
      filter: {
        ...(filter.campaignId && { campaignId: filter.campaignId }),
        ...(filter.userId && { userId: filter.userId }),
      },
    },
    fetchPolicy: "network-only",
    skip: activeTab === "pending",
  });

  const loading = activeTab === "pending" ? pendingLoading : allLoading;
  const error = activeTab === "pending" ? pendingError : allError;
  const refetch = activeTab === "pending" ? refetchPending : refetchAll;

  const getDisplayStatus = (submission: Submission): string => {
    // status가 흔들리더라도 승인 플래그가 true면 '승인'으로 표시
    if (submission.isApproved) return "APPROVED";
    if (submission.status === "REJECTED") return "REJECTED";
    if (submission.isSubmitted) return "SUBMITTED";
    return submission.status;
  };

  const getStatusRank = (status: string): number => {
    switch (status) {
      case "APPROVED":
        return 5;
      case "SUBMITTED":
        return 4;
      case "REJECTED":
        return 3;
      case "EXPIRED":
        return 2;
      case "PENDING":
        return 1;
      default:
        return 0;
    }
  };

  const getComparableTimestamp = (submission: Submission): number => {
    const submittedAt = submission.submittedAt;
    if (!submittedAt) return 0;
    const time = new Date(String(submittedAt)).getTime();
    return Number.isFinite(time) ? time : 0;
  };

  const pickBetterSubmission = (a: Submission, b: Submission): Submission => {
    const aStatus = getDisplayStatus(a);
    const bStatus = getDisplayStatus(b);
    const aRank = getStatusRank(aStatus);
    const bRank = getStatusRank(bStatus);

    if (aRank !== bRank) return aRank > bRank ? a : b;

    const aTime = getComparableTimestamp(a);
    const bTime = getComparableTimestamp(b);
    if (aTime !== bTime) return aTime > bTime ? a : b;

    // 마지막 안전장치: id가 더 큰 것을 우선
    return a.id > b.id ? a : b;
  };

  // 캠페인별로 그룹핑하고, 각 캠페인 내에서 유저별로 그룹핑
  const groupedData = useMemo((): CampaignGroup[] => {
    const submissions =
      activeTab === "pending"
        ? pendingData?.missionAdminPendingSubmissions ?? []
        : allData?.missionAdminAllSubmissions ?? [];

    // 1단계: 캠페인별로 그룹핑
    const campaignMap = new Map<
      number,
      {
        campaign: Submission["participation"]["run"]["campaign"];
        users: Map<number, UserGroup>;
      }
    >();

    submissions.forEach((submission) => {
      const campaignId = submission.participation.run.campaign.id;
      const campaign = submission.participation.run.campaign;

      if (!campaignMap.has(campaignId)) {
        campaignMap.set(campaignId, {
          campaign,
          users: new Map(),
        });
      }

      const campaignData = campaignMap.get(campaignId);
      if (!campaignData) return;

      const user = submission.participation.user;
      const participationId = submission.participationId;

      // 같은 유저라도 participation 단위로 분리해서 표시해야 상태가 섞이지 않음
      if (!campaignData.users.has(participationId)) {
        campaignData.users.set(participationId, {
          user,
          participationId,
          submissions: [],
        });
      }

      const userGroup = campaignData.users.get(participationId);
      if (userGroup) {
        userGroup.submissions.push(submission);
      }
    });

    // Map을 배열로 변환하고 정렬
    const result: CampaignGroup[] = Array.from(campaignMap.entries()).map(
      ([, data]) => {
        const users: UserGroup[] = Array.from(data.users.entries()).map(
          ([, userData]) => {
            // submission들을 stepOrder 순서대로 정렬
            userData.submissions.sort((a, b) => a.stepOrder - b.stepOrder);
            return userData;
          }
        );

        return {
          campaign: data.campaign,
          users,
        };
      }
    );

    return result;
  }, [activeTab, pendingData, allData]);

  // proofData에서 imageUrl 추출하는 헬퍼 함수 (상세 페이지와 동일한 로직)
  const extractImageUrls = (proofData: unknown): string[] => {
    if (!proofData) return [];

    // 1. 문자열인 경우 JSON 파싱 시도
    if (typeof proofData === "string") {
      try {
        const parsed = JSON.parse(proofData);
        return extractImageUrls(parsed); // 재귀적으로 파싱된 값 처리
      } catch {
        // JSON이 아니면 그냥 URL 문자열로 간주 (상대경로 포함)
        const trimmed = proofData.trim();
        return trimmed ? [trimmed] : [];
      }
    }

    // 2. 배열인 경우
    if (Array.isArray(proofData)) {
      const urls: string[] = [];
      proofData.forEach((item) => {
        const extracted = extractImageUrls(item);
        urls.push(...extracted);
      });
      return urls;
    }

    // 3. 객체인 경우
    if (proofData && typeof proofData === "object" && proofData !== null) {
      const obj = proofData as Record<string, unknown>;
      if (typeof obj.imageUrl === "string") {
        return [obj.imageUrl];
      }
      // 객체의 모든 속성에서 재귀적으로 탐색
      for (const key in obj) {
        const value = obj[key];
        if (typeof value === "string") {
          const trimmed = value.trim();
          const isLikelyUrl =
            trimmed.startsWith("http://") ||
            trimmed.startsWith("https://") ||
            trimmed.startsWith("/") ||
            trimmed.startsWith("data:image/");
          if (trimmed && isLikelyUrl) return [trimmed];
        }
        if (value && typeof value === "object") {
          const nestedUrls = extractImageUrls(value);
          if (nestedUrls.length > 0) return nestedUrls;
        }
      }
    }

    return [];
  };

  // 첫 번째 이미지 URL만 반환하는 헬퍼 함수
  const extractImageUrl = (proofData: unknown): string | null => {
    const urls = extractImageUrls(proofData);
    return urls.length > 0 ? urls[0] : null;
  };

  // 상태 칩 렌더링 함수
  const renderStatusChip = (status: string) => {
    const statusConfig: Record<
      string,
      { label: string; bgColor: string; textColor: string }
    > = {
      APPROVED: {
        label: "승인",
        bgColor: "bg-green-100",
        textColor: "text-green-800",
      },
      REJECTED: {
        label: "반려",
        bgColor: "bg-red-100",
        textColor: "text-red-800",
      },
      SUBMITTED: {
        label: "검수대기",
        bgColor: "bg-yellow-100",
        textColor: "text-yellow-800",
      },
      PENDING: {
        label: "미제출",
        bgColor: "bg-gray-100",
        textColor: "text-gray-800",
      },
      EXPIRED: {
        label: "만료",
        bgColor: "bg-orange-100",
        textColor: "text-orange-800",
      },
    };

    const config = statusConfig[status] || {
      label: status,
      bgColor: "bg-gray-100",
      textColor: "text-gray-800",
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}
      >
        {config.label}
      </span>
    );
  };

  const renderProofThumbnail = (submission: Submission) => {
    // proofData가 있으면 무조건 이미지 표시 시도
    if (!submission.proofData) {
      return (
        <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-400">
          -
        </div>
      );
    }

    // 서버 ProofType: NONE | SCREENSHOT | PHOTO | TEXT | URL
    if (
      submission.proofType === "PHOTO" ||
      submission.proofType === "SCREENSHOT"
    ) {
      const imageUrl = extractImageUrl(submission.proofData);

      if (!imageUrl) {
        return (
          <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-400">
            -
          </div>
        );
      }

      return (
        <div
          className="w-16 h-16 rounded overflow-hidden cursor-pointer hover:opacity-80 transition-opacity border border-gray-200"
          onClick={(e) => {
            e.stopPropagation();
            window.open(imageUrl, "_blank");
          }}
        >
          <ResponsiveCDNImage
            src={imageUrl}
            alt="제출 이미지"
            className="w-full h-full object-cover"
            sizeRatio={0.25}
            lazy={false}
            onError={(e) => {
              // 이미지 로드 실패 시 플레이스홀더 표시
              const target = e.currentTarget;
              target.style.display = "none";
              const parent = target.parentElement;
              if (parent) {
                parent.innerHTML =
                  '<div class="w-16 h-16 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-400">오류</div>';
              }
            }}
          />
        </div>
      );
    }

    return (
      <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-400">
        {submission.proofType === "TEXT"
          ? "텍스트"
          : submission.proofType === "URL"
          ? "링크"
          : "-"}
      </div>
    );
  };

  if (loading) return <div className="text-center py-8">로딩 중...</div>;
  if (error)
    return (
      <div className="text-center py-8 text-red-600">에러: {error.message}</div>
    );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">미션 제출 검수</h1>
      </div>

      {/* 탭 */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab("pending")}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === "pending"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              검수 대기
            </button>
            <button
              onClick={() => setActiveTab("all")}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === "all"
                  ? "border-yellow-500 text-yellow-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              전체보기
            </button>
          </nav>
        </div>
      </div>

      {/* 필터 */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              캠페인 ID
            </label>
            <input
              type="number"
              value={filter.campaignId || ""}
              onChange={(e) =>
                setFilter({
                  ...filter,
                  campaignId: e.target.value ? parseInt(e.target.value) : null,
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="캠페인 ID 입력"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              유저 ID
            </label>
            <input
              type="number"
              value={filter.userId || ""}
              onChange={(e) =>
                setFilter({
                  ...filter,
                  userId: e.target.value ? parseInt(e.target.value) : null,
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="유저 ID 입력"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => refetch()}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              검색
            </button>
          </div>
        </div>
      </div>

      {/* 캠페인별 그룹 */}
      {groupedData.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          {activeTab === "pending"
            ? "대기 중인 미션 제출이 없습니다."
            : "표시할 미션 제출이 없습니다."}
        </div>
      ) : (
        <div className="space-y-6">
          {groupedData.map((campaignGroup) => {
            const { campaign, users } = campaignGroup;
            const steps = campaign.steps || [];
            const sortedSteps = [...steps].sort(
              (a, b) => a.stepOrder - b.stepOrder
            );

            // 회차 정보 계산 (첫 번째 submission의 run 기준)
            const firstSubmission = users[0]?.submissions[0];
            let runNumber: number | null = null;
            let runStartAt: string | null = null;
            let runEndAt: string | null = null;

            if (firstSubmission && campaign.runs && campaign.runs.length > 0) {
              const runs = campaign.runs || [];
              const sortedRuns = [...runs].sort(
                (a, b) =>
                  new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
              );
              const currentRun = firstSubmission.participation.run;
              const currentRunIndex = sortedRuns.findIndex(
                (run) => run.id === currentRun.id
              );
              if (currentRunIndex >= 0) {
                runNumber = currentRunIndex + 1;
                runStartAt = currentRun.startAt;
                runEndAt = currentRun.endAt;
              }
            }

            const formatDateOnly = (dateString: string) => {
              return new Date(dateString).toLocaleDateString("ko-KR", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              });
            };

            return (
              <div
                key={campaign.id}
                className="bg-white rounded-lg shadow overflow-hidden"
              >
                {/* 캠페인 헤더 */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-4">
                    {campaign.productImageUrl && (
                      <ResponsiveCDNImage
                        src={campaign.productImageUrl}
                        alt={campaign.productName}
                        className="w-12 h-12 object-cover rounded"
                        sizeRatio={0.25}
                        lazy={false}
                      />
                    )}
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {campaign.productName}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {campaign.brand.name}
                      </p>
                      {runNumber && runStartAt && runEndAt && (
                        <p className="text-sm text-blue-600 mt-1">
                          회차: {runNumber}회 ({formatDateOnly(runStartAt)} ~{" "}
                          {formatDateOnly(runEndAt)})
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* 테이블 */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          유저이름
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          번호
                        </th>
                        {sortedSteps.map((step) => (
                          <th
                            key={step.id}
                            className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {step.title || `미션${step.stepOrder}`}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((userGroup) => {
                        const { user, participationId, submissions } =
                          userGroup;

                        // pending 탭에서는 쿼리가 "대기 중인 제출"만 내려줄 수 있으므로,
                        // participation.submissions(전체 스텝 상태)를 우선 사용
                        const participationSubmissions: Submission[] = (() => {
                          const first = submissions[0];
                          if (!first) return [];

                          const participation =
                            first.participation as unknown as {
                              submissions?: Submission[];
                            };
                          if (
                            Array.isArray(participation.submissions) &&
                            participation.submissions.length > 0
                          ) {
                            return participation.submissions;
                          }
                          return submissions;
                        })();

                        const submissionByMissionStepId = new Map<
                          number,
                          Submission
                        >();
                        const submissionByStepOrder = new Map<
                          number,
                          Submission
                        >();
                        participationSubmissions.forEach((sub) => {
                          const existingByStepId =
                            submissionByMissionStepId.get(sub.missionStepId);
                          submissionByMissionStepId.set(
                            sub.missionStepId,
                            existingByStepId
                              ? pickBetterSubmission(existingByStepId, sub)
                              : sub
                          );

                          const existingByOrder = submissionByStepOrder.get(
                            sub.stepOrder
                          );
                          submissionByStepOrder.set(
                            sub.stepOrder,
                            existingByOrder
                              ? pickBetterSubmission(existingByOrder, sub)
                              : sub
                          );
                        });

                        const detailUrl = participationId
                          ? `/ops/mission/reviews/submissions/${participationId}`
                          : "#";

                        return (
                          <tr
                            key={participationId}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              if (participationId) {
                                navigate(detailUrl);
                              }
                            }}
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {user.name || `유저 ${user.id}`}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-500">
                                {user.phone || "-"}
                              </div>
                            </td>
                            {sortedSteps.map((step) => {
                              const submission =
                                submissionByMissionStepId.get(step.id) ??
                                submissionByStepOrder.get(step.stepOrder);

                              if (!submission) {
                                return (
                                  <td
                                    key={step.id}
                                    className="px-4 py-3 text-center"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="flex flex-col items-center gap-2">
                                      <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-400">
                                        -
                                      </div>
                                      {renderStatusChip("PENDING")}
                                    </div>
                                  </td>
                                );
                              }

                              return (
                                <td
                                  key={step.id}
                                  className="px-4 py-3 text-center"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="flex flex-col items-center gap-2">
                                    {renderProofThumbnail(submission)}
                                    {renderStatusChip(
                                      getDisplayStatus(submission)
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SubmissionsList;
